/**
 * Typed JSON-LD schema builders.
 *
 * Each builder returns a plain object that can be stringified into a
 * <script type="application/ld+json"> tag (handled by <Seo />).
 *
 * Keep these output shapes valid against schema.org - Rich Results Test
 * is the source of truth for what passes Google's parser.
 */

const SITE_URL = 'https://www.recovry.in';

export type Json = Record<string, unknown>;

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export function breadcrumbSchema(items: BreadcrumbItem[]): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function faqSchema(faqs: FaqItem[]): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

export interface ServiceSchemaInput {
  name: string;
  description: string;
  /** absolute URL to the service page */
  url: string;
  /** optional area served - name + geo */
  areaServed?: { name: string; lat?: number; lng?: number };
  /** optional price range "₹500-₹1500" */
  priceRange?: string;
}

export function serviceSchema(input: ServiceSchemaInput): Json {
  const schema: Json = {
    '@context': 'https://schema.org',
    '@type': 'MedicalTherapy',
    name: input.name,
    description: input.description,
    url: input.url,
    provider: {
      '@type': 'MedicalOrganization',
      name: 'Recovry',
      url: SITE_URL,
    },
  };

  if (input.areaServed) {
    schema.areaServed = {
      '@type': 'Place',
      name: input.areaServed.name,
      ...(input.areaServed.lat && input.areaServed.lng
        ? {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: input.areaServed.lat,
              longitude: input.areaServed.lng,
            },
          }
        : {}),
    };
  }

  if (input.priceRange) schema.offers = { '@type': 'Offer', priceRange: input.priceRange };

  return schema;
}

export interface MedicalConditionInput {
  name: string;
  description: string;
  url: string;
  symptoms?: string[];
  causes?: string[];
}

export function medicalConditionSchema(input: MedicalConditionInput): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalCondition',
    name: input.name,
    description: input.description,
    url: input.url,
    ...(input.symptoms?.length
      ? {
          signOrSymptom: input.symptoms.map((s) => ({
            '@type': 'MedicalSymptom',
            name: s,
          })),
        }
      : {}),
    ...(input.causes?.length
      ? {
          cause: input.causes.map((c) => ({
            '@type': 'MedicalCause',
            name: c,
          })),
        }
      : {}),
  };
}

export interface LocalBusinessInput {
  name: string;
  description?: string;
  url: string;
  image?: string[];
  telephone?: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  geo?: { lat: number; lng: number };
  priceRange?: string;
  openingHours?: { day: string; opens: string; closes: string }[];
  aggregateRating?: { value: number; count: number };
  /** First-party reviews only (written on Recovry after a booking). */
  reviews?: { author: string; rating: number; body?: string; date?: string }[];
  sameAs?: string[];
}

export function localBusinessSchema(input: LocalBusinessInput): Json {
  return {
    '@context': 'https://schema.org',
    '@type': ['MedicalBusiness', 'Physiotherapy'],
    name: input.name,
    ...(input.description ? { description: input.description } : {}),
    url: input.url,
    ...(input.image?.length ? { image: input.image } : {}),
    ...(input.telephone ? { telephone: input.telephone } : {}),
    address: {
      '@type': 'PostalAddress',
      streetAddress: input.address.streetAddress,
      addressLocality: input.address.addressLocality,
      ...(input.address.addressRegion ? { addressRegion: input.address.addressRegion } : {}),
      ...(input.address.postalCode ? { postalCode: input.address.postalCode } : {}),
      addressCountry: input.address.addressCountry || 'IN',
    },
    ...(input.geo
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: input.geo.lat,
            longitude: input.geo.lng,
          },
        }
      : {}),
    ...(input.priceRange ? { priceRange: input.priceRange } : {}),
    ...(input.openingHours?.length
      ? {
          openingHoursSpecification: input.openingHours.map((h) => ({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: h.day,
            opens: h.opens,
            closes: h.closes,
          })),
        }
      : {}),
    ...(input.aggregateRating
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: input.aggregateRating.value,
            reviewCount: input.aggregateRating.count,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
    ...(input.reviews?.length
      ? {
          review: input.reviews.map((r) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: r.author },
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
            ...(r.body ? { reviewBody: r.body } : {}),
            ...(r.date ? { datePublished: r.date } : {}),
          })),
        }
      : {}),
    ...(input.sameAs?.length ? { sameAs: input.sameAs } : {}),
  };
}

export interface ItemListInput {
  name: string;
  url: string;
  items: { name: string; url: string }[];
}

export function itemListSchema(input: ItemListInput): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: input.name,
    url: input.url,
    itemListElement: input.items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      url: item.url,
    })),
  };
}

export interface CollectionPageInput {
  name: string;
  description: string;
  url: string;
}

export function collectionPageSchema(input: CollectionPageInput): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: input.name,
    description: input.description,
    url: input.url,
  };
}

export interface MedicalWebPageInput {
  name: string;
  description: string;
  url: string;
  about?: string;
  reviewedBy?: { name: string; jobTitle?: string };
  lastReviewed?: string;
}

export function medicalWebPageSchema(input: MedicalWebPageInput): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: input.name,
    description: input.description,
    url: input.url,
    ...(input.about ? { about: { '@type': 'MedicalCondition', name: input.about } } : {}),
    ...(input.reviewedBy
      ? {
          reviewedBy: {
            '@type': 'Person',
            name: input.reviewedBy.name,
            ...(input.reviewedBy.jobTitle ? { jobTitle: input.reviewedBy.jobTitle } : {}),
          },
        }
      : {}),
    ...(input.lastReviewed ? { lastReviewed: input.lastReviewed } : {}),
    inLanguage: 'en-IN',
  };
}

export interface ClinicListCentre {
  id: string;
  name: string;
  address: string;
  city?: string;
  phone?: string;
  location?: { lat: number; lng: number };
  images?: string[];
  image?: string;
  services: { name?: string; price?: number; reviews?: { rating?: number; verified?: boolean }[] }[];
  reviews?: { rating?: number; verified?: boolean }[];
  therapists?: { name: string; qualification?: string; specialisation?: string }[];
}

export interface ClinicListInput {
  name: string;
  url: string;
  centres: ClinicListCentre[];
  /** Site default photo, used when a clinic has uploaded none (`image` is required). */
  defaultImage?: string;
  /** Absolute URL for each clinic. */
  linkFor: (centre: ClinicListCentre) => string;
}

/**
 * The clinics listed on a location page, as an ItemList of MedicalBusiness
 * entities - the pattern Practo, CB Physiotherapy and JustDial use on their
 * ranking city pages. A clinic's aggregateRating is included only when it has
 * real reviews, and those reviews are rendered on the page; a rating with no
 * visible reviews is what Google's review-snippet policy penalises.
 */
export function clinicListSchema(input: ClinicListInput): Json {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: input.name,
    url: input.url,
    numberOfItems: input.centres.length,
    itemListElement: input.centres.map((c, i) => {
      // Schema ratings: first-party (verified) reviews only; see firstPartyReviews.
      const ratings = [...c.services.flatMap((s) => s.reviews ?? []), ...(c.reviews ?? [])]
        .filter((r) => r.verified === true)
        .map((r) => r.rating)
        .filter((r): r is number => typeof r === 'number');
      const prices = c.services.map((s) => s.price).filter((p): p is number => !!p && p > 0);
      const image = c.images?.[0] || c.image || input.defaultImage;
      const item: Record<string, unknown> = {
        '@type': ['MedicalBusiness', 'Physiotherapy'],
        name: c.name,
        url: input.linkFor(c),
        address: {
          '@type': 'PostalAddress',
          streetAddress: c.address,
          ...(c.city ? { addressLocality: c.city } : {}),
          addressCountry: 'IN',
        },
      };
      if (image) item.image = image;
      if (c.phone) item.telephone = c.phone;
      if (c.location) {
        item.geo = { '@type': 'GeoCoordinates', latitude: c.location.lat, longitude: c.location.lng };
      }
      if (prices.length) {
        const lo = Math.min(...prices);
        const hi = Math.max(...prices);
        item.priceRange = lo === hi ? `₹${lo}` : `₹${lo}-₹${hi}`;
      }
      if (ratings.length) {
        const mean = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        item.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: Math.round(mean * 10) / 10,
          reviewCount: ratings.length,
          bestRating: 5,
          worstRating: 1,
        };
      }
      if (c.therapists?.length) {
        // Physiotherapists as Physician: schema.org has no closer type, and it
        // is the convention Practo / CB / JustDial all use.
        item.employee = c.therapists.map((t) => ({
          '@type': 'Physician',
          name: t.name,
          ...(t.qualification ? { jobTitle: t.qualification } : {}),
          medicalSpecialty: t.specialisation || 'Physiotherapy',
        }));
      }
      return { '@type': 'ListItem', position: i + 1, item };
    }),
  };
}
